import { useEffect, useRef } from 'react';

import { Message } from '../../conversations/types/Conversation';
import { isVisitorWebWidgetMessage } from '../utils/webWidgetMessageAlignment';
import {
  bindWebWidgetNotificationSoundUnlock,
  playWebWidgetNotificationSound,
} from '../utils/webWidgetNotificationSound';

interface UseWebWidgetNotificationSoundOptions {
  messages: Message[];
  sessionId: string | null;
  isReady: boolean;
  playSound?: () => Promise<void> | void;
}

export const useWebWidgetNotificationSound = ({
  messages,
  sessionId,
  isReady,
  playSound = playWebWidgetNotificationSound,
}: UseWebWidgetNotificationSoundOptions): void => {
  const seenIncomingIdsRef = useRef<Set<string>>(new Set());
  const primedSessionIdRef = useRef<string | null>(null);

  useEffect(() => bindWebWidgetNotificationSoundUnlock(), []);

  useEffect(() => {
    if (!sessionId) {
      seenIncomingIdsRef.current = new Set();
      primedSessionIdRef.current = null;
      return;
    }

    if (!isReady) {
      return;
    }

    const incomingIds = messages
      .filter(message => !isVisitorWebWidgetMessage(message) && message.id.trim())
      .map(message => message.id);

    if (primedSessionIdRef.current !== sessionId) {
      seenIncomingIdsRef.current = new Set(incomingIds);
      primedSessionIdRef.current = sessionId;
      return;
    }

    const unseenIds = incomingIds.filter(id => !seenIncomingIdsRef.current.has(id));
    incomingIds.forEach(id => seenIncomingIdsRef.current.add(id));

    if (unseenIds.length > 0) {
      void playSound();
    }
  }, [isReady, messages, playSound, sessionId]);
};
